import { useState, useRef, useCallback } from 'react';
import { EVIWebAudioPlayer } from 'hume';

export const useEVIConnection = (config, audioService, reminderSystem = null) => {
  // WebSocket and media refs
  const wsRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const playerRef = useRef(null);

  // Connection states
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [isEviSpeaking, setIsEviSpeaking] = useState(false);
  const [currentEmotion, setCurrentEmotion] = useState('');
  const [isInitializing, setIsInitializing] = useState(false);
  
  // Response states
  const [response, setResponse] = useState('');
  const [showResponse, setShowResponse] = useState(false);

  // Chat management
  const [currentChatId, setCurrentChatId] = useState(null);
  const [currentChatGroupId, setCurrentChatGroupId] = useState(null);
  const [isResuming, setIsResuming] = useState(false);

  const handleEVIMessage = useCallback(async (data) => {
    console.log('EVI Message:', data);
    
    switch (data.type) {
      case 'chat_metadata':
        if (data.chat_id) {
          setCurrentChatId(data.chat_id);
        }
        
        if (data.chat_group_id) {
          setCurrentChatGroupId(data.chat_group_id);
          
          const chatGroupInfo = {
            id: data.chat_group_id,
            lastUsed: new Date().toISOString(),
            name: `Chat ${new Date().toLocaleString()}`,
            chatId: data.chat_id || null
          };
          
          localStorage.setItem('hume-latest-chat-group', JSON.stringify(chatGroupInfo));
        }
        break;

      case 'user_message':
        console.log('User message received:', data.message?.content);
        audioService.resetPreResponseFlag();
        
        const userContent = data.message?.content || '';
        
        // Check for reminder requests in user speech if reminderSystem is available
        if (reminderSystem && userContent) {
          reminderSystem.checkForReminderRequest(userContent);
        }
        
        if (data.models?.prosody?.scores && Array.isArray(data.models.prosody.scores) && data.models.prosody.scores.length > 0) {
          const topEmotion = data.models.prosody.scores.reduce((max, emotion) => 
            emotion.score > max.score ? emotion : max
          );
          setCurrentEmotion(topEmotion.name);
        }
        break;
        
      case 'assistant_message':
        console.log('Assistant message received:', data.message?.content);
        
        if (!audioService.wasPreResponseSoundPlayed()) {
          audioService.setPreResponseFlag();
          audioService.playPreResponseSound().catch(console.error);
        }
        
        setResponse(data.message?.content || '');
        setShowResponse(true);
        setIsEviSpeaking(true);
        break;
        
      case 'audio_output':
        try {
          if (data.data && audioService.getAudioStates().soundEnabled) {
            console.log('Playing audio output, length:', data.data.length);
            audioService.enqueueAudio(data.data);
          }
        } catch (err) {
          console.error('Audio playback error:', err);
        }
        break;
        
      case 'assistant_end':
        console.log('Assistant finished speaking');
        setIsEviSpeaking(false);
        
        setTimeout(() => {
          setShowResponse(false);
          setResponse('');
        }, 2000);
        
        setTimeout(() => {
          if (isConnected && !isRecording) {
            console.log('Resuming recording after assistant end');
            startRecording();
          }
        }, 1000);
        break;
        
      case 'user_interruption':
        console.log('User interrupted assistant');
        setIsEviSpeaking(false);
        
        audioService.resetPreResponseFlag();
        audioService.stopAllAudio();
        
        if (playerRef.current) {
          playerRef.current.stop();
        }
        
        setShowResponse(false);
        setResponse('');
        
        setTimeout(() => {
          if (isConnected && !isRecording) {
            console.log('Ensuring recording continues after interruption');
            startRecording();
          }
        }, 500);
        break;
        
      case 'connection_end':
      case 'call_end':
        console.log('Connection/call ended');
        
        setIsConnected(false);
        setConnectionStatus('disconnected');
        setIsEviSpeaking(false);
        
        if (isRecording) {
          stopRecording();
        }
        
        audioService.stopAllAudio();
        break;
        
      case 'error':
        console.error('EVI Error received:', data.message || data.error);
        
        if (data.code === 'rate_limit_exceeded') {
          throw new Error('Rate limit exceeded. Please wait before trying again.');
        } else if (data.code === 'authentication_failed') {
          throw new Error('Authentication failed. Please check your API key.');
          setConnectionStatus('error');
        }
        break;
        
      default:
        console.log('Unknown EVI message type:', data.type, data);
    }
  }, [isConnected, isRecording, audioService]);

  const connectToEVI = useCallback(async (resumeChatGroupId = null) => {
    try {
      setConnectionStatus('connecting');
      setIsInitializing(true);

      if (!config.HUME_API_KEY || config.HUME_API_KEY === 'hj') {
        throw new Error('Please set your Hume API key in the environment variables');
      }

      let wsUrl = `${config.EVI_WS_URL}?apiKey=${config.HUME_API_KEY}&config_id=${config.CONFIG_ID}`;
      if (resumeChatGroupId) {
        wsUrl += `&resumed_chat_group_id=${resumeChatGroupId}`;
        setIsResuming(true);
      }

      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = async () => {
        setIsConnected(true);
        setConnectionStatus('connected');
        console.log('Connected to Hume EVI');

        playerRef.current = new EVIWebAudioPlayer();
        
        try {
          await audioService.initAudioContext();
          await audioService.playStartupSound(1000);
        } catch (error) {
          console.error('Audio initialization failed:', error);
        } finally {
          setIsInitializing(false);
          setTimeout(startRecording, 400);
        }

        setIsResuming(false);
      };

      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        handleEVIMessage(data);
      };

      wsRef.current.onclose = () => {
        setIsConnected(false);
        setConnectionStatus('disconnected');
        setIsRecording(false);
        setIsEviSpeaking(false);
        setIsInitializing(false);
        console.log('WebSocket closed');
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionStatus('error');
        setIsInitializing(false);
        throw new Error('Connection failed. Check your API key.');
      };

    } catch (err) {
      setConnectionStatus('error');
      setIsInitializing(false);
      throw err;
    }
  }, [config, handleEVIMessage, audioService]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0 && wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(event.data);
        }
      };

      mediaRecorderRef.current.start(100);
      setIsRecording(true);
      console.log('Recording started');

    } catch (error) {
      console.error('Failed to start recording:', error);
      throw new Error('Microphone access required');
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
  }, [isRecording]);

  const cleanup = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
    if (playerRef.current) {
      playerRef.current.stop();
    }
  }, []);

  const reset = useCallback(() => {
    setResponse('');
    setShowResponse(false);
    setCurrentEmotion('');
    setCurrentChatId(null);
    setCurrentChatGroupId(null);
    setIsEviSpeaking(false);
  }, []);

  return {
    // States
    isConnected,
    isRecording,
    connectionStatus,
    isEviSpeaking,
    currentEmotion,
    isInitializing,
    response,
    showResponse,
    currentChatId,
    currentChatGroupId,
    isResuming,
    
    // Actions
    connectToEVI,
    startRecording,
    stopRecording,
    cleanup,
    reset
  };
};